import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatDateTime } from '@/lib/utils';

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'warning' | 'outline'> = {
  completed: 'success',
  failed: 'destructive',
  running: 'warning',
  queued: 'secondary',
  cancelled: 'outline',
};

const MODE_LABEL: Record<string, string> = {
  simple: 'Simple',
  advanced: 'Advanced',
  expert: 'Expert',
  grid: 'Grid',
};

export function BacktestPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [userIdFilter, setUserIdFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'backtests', page, statusFilter, modeFilter, userIdFilter],
    queryFn: () =>
      adminClient.get('/admin/backtests', {
        params: {
          page,
          per_page: 20,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          mode: modeFilter !== 'all' ? modeFilter : undefined,
          user_id: userIdFilter ? parseInt(userIdFilter) : undefined,
        },
      }).then((r) => r.data),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">回測歷史（全用戶）</h1>
        <span className="text-sm text-muted-foreground">總計：{data?.total ?? 0} 筆</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="狀態" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有狀態</SelectItem>
            <SelectItem value="completed">完成</SelectItem>
            <SelectItem value="running">執行中</SelectItem>
            <SelectItem value="queued">排隊中</SelectItem>
            <SelectItem value="failed">失敗</SelectItem>
            <SelectItem value="cancelled">已取消</SelectItem>
          </SelectContent>
        </Select>
        <Select value={modeFilter} onValueChange={(v) => { setModeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="模式" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有模式</SelectItem>
            <SelectItem value="simple">Simple</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
            <SelectItem value="expert">Expert</SelectItem>
            <SelectItem value="grid">Grid</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="用戶 ID..."
          value={userIdFilter}
          onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
          className="w-[120px]"
          type="number"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>用戶</TableHead>
                  <TableHead>商品</TableHead>
                  <TableHead>週期</TableHead>
                  <TableHead>模式</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>進度</TableHead>
                  <TableHead>建立時間</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((bt: any) => (
                  <TableRow key={bt.id}>
                    <TableCell className="text-sm">
                      <p>{bt.user_email ?? `uid:${bt.user_id}`}</p>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{bt.symbol}</TableCell>
                    <TableCell className="text-sm">{bt.timeframe}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{MODE_LABEL[bt.mode] ?? bt.mode}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[bt.status] ?? 'secondary'}>
                        {bt.status === 'completed' ? '完成'
                          : bt.status === 'running' ? '執行中'
                          : bt.status === 'queued' ? '排隊'
                          : bt.status === 'failed' ? '失敗'
                          : bt.status === 'cancelled' ? '取消'
                          : bt.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {bt.status === 'running' ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-1.5 w-20">
                            <div
                              className="bg-brand-primary h-1.5 rounded-full"
                              style={{ width: `${bt.progress * 100}%` }}
                            />
                          </div>
                          <span className="text-xs">{(bt.progress * 100).toFixed(0)}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(bt.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>下一頁</Button>
        </div>
      )}
    </div>
  );
}
